
import React from "react";
import { CaseStudyLayout } from "@/components/case-studies/CaseStudyLayout";
import { ProcessStep } from "@/components/case-studies/ProcessStep";
import { CaseMetrics } from "@/components/case-studies/CaseMetrics";
import { BookOpen, CalendarCheck, Compass, Target, TrendingUp } from "lucide-react";

const ServiceBusiness = () => {
  const metrics = [
    {
      value: "$314",
      label: "Cost Per Lead",
      description: "From targeted YouTube advertising"
    },
    {
      value: "95%",
      label: "Show Rate",
      description: "For scheduled sales calls"
    },
    {
      value: "22%",
      label: "Close Rate",
      description: "Significantly above industry average"
    },
    {
      value: "$10k",
      label: "Average Contract Value",
      description: "For B2B content marketing services"
    },
    {
      value: "15.3x",
      label: "Return on Ad Spend",
      description: "Factoring in show rate and close rate"
    },
    {
      value: "4.9/5",
      label: "Client Satisfaction",
      description: "With the acquisition process"
    }
  ];

  return (
    <CaseStudyLayout
      title="Scaling a B2B Marketing Agency"
      industry="Service Business"
    >
      <div className="max-w-4xl mx-auto mb-16">
        <p className="text-xl leading-relaxed mb-8 fade-in">
          For this B2B marketing agency specializing in content creation for startups, 
          we developed a high-ticket client acquisition system using YouTube advertising. 
          With a cost per lead of $314, we achieved an exceptional 95% show rate for sales 
          calls and a 22% close rate for contracts averaging $10,000 in value.
        </p>
        
        <CaseMetrics metrics={metrics} />
        
        <div className="my-16">
          <h2 className="mb-10 fade-in">Our Process</h2>
          
          <ProcessStep
            number="01"
            title="Strategic YouTube Campaign Development"
            description="We created targeted YouTube ad campaigns focused on reaching B2B startup founders and marketing executives. Our video creative highlighted specific pain points around content creation and demonstrated our client's expertise in this niche, generating high-intent leads at a cost of $314 each."
            image="/youtube-ads.webp"
            icon={<Target className="h-6 w-6" />}
          />
          
          <ProcessStep
            number="02"
            title="High-Value Offer Creation"
            description="We developed a compelling lead magnet and call-to-action focused on content strategy assessment for startups. This approach pre-qualified leads by attracting only those with serious interest in professional content marketing services."
            image="/offer-creation.webp"
            icon={<BookOpen className="h-6 w-6" />}
            reversed
          />
          
          <ProcessStep
            number="03"
            title="Multi-Stage Qualification"
            description="Using Vail AI, we implemented a sophisticated qualification process that assessed budget readiness, decision-making authority, timeline, and specific content needs. This multi-touchpoint approach ensured that only highly qualified prospects were advanced to the sales team."
            image="/qualification-process.webp"
            icon={<Compass className="h-6 w-6" />}
          />
          
          <ProcessStep
            number="04"
            title="AI-Driven Appointment Setting"
            description="Vail handled all aspects of appointment scheduling, including finding optimal times, sending calendar invitations, and providing pre-call preparation materials. This thorough approach contributed to the exceptional 95% show rate for sales calls."
            image="/appointment-setting.webp"
            icon={<CalendarCheck className="h-6 w-6" />}
            reversed
          />
          
          <ProcessStep
            number="05"
            title="Sales Team Enablement"
            description="We provided the sales team with comprehensive lead intelligence gathered during the AI qualification process. This included specific pain points, budget parameters, and content priorities—giving the sales team a significant advantage in closing deals, leading to a 22% close rate."
            image="/sales-enablement.webp"
            icon={<TrendingUp className="h-6 w-6" />}
          />
        </div>
        
        <div className="bg-secondary/30 p-8 rounded-2xl my-16 fade-in">
          <h3 className="mb-4">The Results</h3>
          <p className="mb-4">
            Our comprehensive client acquisition system transformed the agency's growth trajectory. 
            With a $314 cost per lead, 95% show rate, and 22% close rate on $10,000 contracts, 
            the return on advertising spend was exceptional.
          </p>
          <p>
            By implementing AI-powered qualification and sophisticated appointment setting, 
            we not only increased the volume of clients but dramatically improved the quality 
            of sales conversations. The sales team was able to focus exclusively on highly 
            qualified, well-prepared prospects, optimizing their time and increasing their 
            conversion effectiveness.
          </p>
        </div>
      </div>
    </CaseStudyLayout>
  );
};

export default ServiceBusiness;
